import React, { useState } from 'react';
import { FlaskConical, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import BacktestLab from './ops/BacktestLab';
import BacktestLabFTMO from './ops/BacktestLabFTMO';
import { SniperOptimizer } from './ops/SniperOptimizer';
import { SniperV2StressLab } from './ops/SniperV2StressLab';
import { BOT_LABELS, BOT_COLORS } from './ops/ops_helpers';

const SUPPORTED_BOTS = ['hyperliquid', 'ftmo', 'sniper', 'sniper_v2', 'sentimental'];

export function AdminOpsBacktest() {
  const navigate = useNavigate();
  const [botId, setBotId] = useState('hyperliquid');

  return (
    <div className="min-h-screen bg-black text-zinc-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/admin/ops')}
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="back-to-ops-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Ops Center
            </Button>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-cyan-400" />
              Backtest Lab
            </h1>
            <Badge variant="outline" className="border-cyan-700 text-cyan-300">v2</Badge>
          </div>

          {/* Bot selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400">Bot:</span>
            <Select value={botId} onValueChange={setBotId}>
              <SelectTrigger className="h-9 w-56 bg-zinc-900 border-zinc-700" data-testid="bot-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_BOTS.map((b) => (
                  <SelectItem key={b} value={b} data-testid={`bot-option-${b}`}>
                    <span style={{ color: BOT_COLORS[b] }}>●</span> {BOT_LABELS[b] || b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Description */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Laboratorio de estrategias</CardTitle>
            <CardDescription className="text-xs">
              {botId === 'ftmo'
                ? 'XAUUSD London Breakout Fixed Pips. Prueba SL/TP/spread/ventana London sobre data H1 historica del servidor. Validador FTMO Compliance incluido.'
                : botId === 'sniper'
                ? 'Sniper XAUUSD M1. Sube el CSV del Optimizador de MT5 y obten el ranking de sets: robustos / para fondeadas / agresivos / descartados por sobreoptimizacion, con score de robustez y exportacion .set.'
                : botId === 'sniper_v2'
                ? 'Sniper v2 — Gold Momentum Selective. Stress test de costes: sube el CSV por-operacion (Sniper_v2_trades_breakdown.csv) de cada escenario (real / spread_x1.5 / spread_x2 / oos) y obten net/PF/expectancy/DD tras costes, desglose BUY-SELL / hora / dia y el veredicto de aprobacion para real.'
                : botId === 'sentimental'
                ? 'Sentimental BTC M15 (API de sentimiento). Como depende de una API en vivo, MT5 no lo backtestea: genera el CSV con el optimizador Python (reproduce las señales sobre el historico del motor) y subelo aqui. Mismo ranking robustos / fondeadas / agresivos y exportacion .set de inputs del EA.'
                : 'Hyperliquid SMA Cross multi-crypto. Prueba SL/RR/BE/filtros sobre datos reales sin tocar produccion.'}
              {botId !== 'sniper' && botId !== 'sentimental' && ' Los runs se guardan en MongoDB. Click sobre 2 o 3 runs del historial para comparar side-by-side.'}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Backtest Lab Component (routing) */}
        {botId === 'ftmo'
          ? <BacktestLabFTMO />
          : botId === 'sniper'
          ? <SniperOptimizer botId="sniper" />
          : botId === 'sniper_v2'
          ? <SniperV2StressLab />
          : botId === 'sentimental'
          ? <SniperOptimizer botId="sentimental" />
          : <BacktestLab botId={botId} />}
      </div>
    </div>
  );
}

export default AdminOpsBacktest;
